
/**
 * Which CertainPath accounts the list shows.
 *
 * The three states are disjoint and assigned by precedence — a row holding one
 * unconfirmed mapping alongside two confirmed ones is `review`, because that is
 * the work still outstanding. They therefore sum to the catalog, which the
 * previous split did not: "Needs attention" mixed empty rows with unreviewed
 * ones, and "Has accounts" overlapped both, so the counts never reconciled.
 */
export type LedgerViewTab = "all" | "unmapped" | "review" | "confirmed";

/**
 * Ordered by the life of a mapping — everything, then nothing mapped, then
 * mapped but unconfirmed, then done — rather than by urgency. The view opens on
 * whichever tab holds the work, so the leftmost slot does not have to.
 */
export const LEDGER_VIEW_TABS: { id: LedgerViewTab; label: string }[] = [
  { id: "all", label: "All accounts" },
  { id: "unmapped", label: "Not mapped" },
  { id: "review", label: "Requires review" },
  { id: "confirmed", label: "Confirmed" },
];

/**
 * The count badge on each tab, coloured by what the tab holds so the row reads
 * as a state of the work rather than four identical grey pills.
 *
 * `all` stays neutral — it is a view, not a state, and tinting it would compete
 * with the three that mean something. Only the inactive tab uses these; the
 * selected tab's badge goes white on the crimson fill, which the
 * `group-data-[state=on]` variants override with higher specificity.
 */
export const LEDGER_TAB_BADGE_CLASSES: Record<LedgerViewTab, string> = {
  all: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300",
  // Solid fills, and borrowed rather than invented: the same crimson the
  // selected tab fills with, and the same blue the unmapped notice draws along
  // its bottom edge, so a colour means one thing across the section.
  unmapped: "bg-primary text-white",
  // Gold takes dark text — white on a fill this bright fails contrast outright.
  review: "bg-amber-400 text-amber-950",
  // Blue rather than the emerald "confirmed" carries elsewhere: it reads as
  // settled without turning the row into a wall of green next to the success
  // band, and keeps the three meaningful tabs in three distinct hues.
  confirmed: "bg-blue-500 text-white",
};

/**
 * Where a row sits, most-urgent-wins. `all` is a view rather than a state, so
 * it is never returned here.
 */
export const ledgerRowTab = (
  mappedCount: number,
  pendingCount: number,
): Exclude<LedgerViewTab, "all"> => {
  if (mappedCount === 0) {
    return "unmapped";
  }

  return pendingCount > 0 ? "review" : "confirmed";
};

/**
 * The "a machine did this" treatment, worn by the Auto-map button.
 *
 * A gradient ring around a flat fill, which CSS cannot express with
 * border-color: the fill is clipped to padding-box and the gradient to
 * border-box, so the gradient shows only where the transparent border sits.
 *
 * The ring itself lives in `index.css` as `.ai-gradient-ring`, because it turns
 * — and the rotation needs `@property` and `@keyframes`, neither of which is
 * expressible as a Tailwind utility. Only the text colours stay here.
 */
export const AI_GRADIENT_RING_CLASSES = [
  "ai-gradient-ring",
  "text-indigo-800",
  "dark:text-white",
].join(" ");

/**
 * A CertainPath row's state, in the order the member should care about it.
 *
 * `gap` is the only one that means "you must act": a KPI names this account and
 * nothing is in it. `pending` means a suggestion is waiting to be accepted.
 *
 * `empty` used to be silent, on the grounds that plenty of accounts are
 * legitimately empty for any given member. That is true of an account nothing
 * reads and false of one a KPI sums — leaving Other Operating Expenses empty
 * blanks no card, it makes Net Profit % read high — and rendering the two
 * identically is what taught members that an unbadged row was optional.
 *
 * So `empty` stays one state and carries a note when the row is summed. Not a
 * fifth state: the row's own status is unchanged, and what differs is a fact
 * about the catalog rather than about the member's progress.
 */
export type LedgerRowState = "gap" | "pending" | "filled" | "empty";

/**
 * Every row carries the left border so text does not shift by 4px when a row
 * changes state. Side-specific `border-l-*` is deliberate — a bare
 * `border-rose-400` would repaint the row's bottom border too.
 */
export const LEDGER_ROW_CLASSES: Record<LedgerRowState, string> = {
  // A gap is the only state worth tinting — it is the one the member has to
  // act on, and the tint is what lets them find it by scrolling rather than by
  // reading every row. Everything else carries the accent bar alone, so the
  // list stays white and the chips inside it keep their contrast.
  gap: "border-l-4 border-l-rose-400 bg-rose-50/50 dark:bg-rose-950/20",
  pending: "border-l-4 border-l-amber-400",
  filled: "border-l-4 border-l-transparent",
  empty: "border-l-4 border-l-transparent",
};

/**
 * What a section heading says when every account in one of its required groups
 * is empty.
 *
 * Keyed by the group key the API returns. These sit on the heading rather than
 * on a row because any one member satisfies the requirement — telling a member
 * who keeps only owner capital that Retained Earnings is missing would be a gap
 * they can never close.
 *
 * Only two groups are left. Revenue, liabilities and kept profit are each a
 * single CertainPath account now, so the API marks their rows required outright
 * and never returns them as a group key.
 */
export const REQUIRED_GROUP_COPY: Record<string, string> = {
  opex: "Nothing is mapped to operating expenses yet. Days Cash on Hand measures how long your cash lasts against what you spend, so it needs at least one.",
  equity:
    "Nothing is mapped to equity yet. Debt to Equity compares what you owe against what you own, so it needs at least one.",
};

export const DO_NOT_MAP_CODE = "do_not_map";

/**
 * What belongs in a CertainPath account, in the member's own vocabulary.
 *
 * A row earns one when its section asks the member to *sort* between rows, not
 * when it asks them to *find* one. Direct Costs and Operating Expenses are
 * sorting problems: several plausible homes, of which only some are named by a
 * KPI, and one is the residual that cannot say so in its own label. Assets,
 * Liabilities and Equity are find-the-name — QuickBooks uses the same words the
 * labels do, and a description there would only restate the label. Without that
 * line every row ends up carrying grey text and none of it gets read.
 *
 * Do Not Map is the exception to the rule and earns one on consequence instead:
 * it drops an account from every KPI and from both sides of reconciliation.
 * Nothing else on the page says so, which is why the description carries it.
 */
export const LEDGER_ACCOUNT_DESCRIPTIONS: Record<string, string> = {
  field_labor:
    "Wages and burden for technicians and installers — everyone whose time goes to a job.",
  materials_equipment:
    "Parts, equipment and materials that go into the jobs you run.",
  other_direct_cost:
    "Permits, subcontractors, equipment rental, dump fees — job costs that aren't wages or parts.",
  office_labor:
    "Wages and burden for owners, officers, CSR, dispatch and management staff — everyone not on a job.",
  marketing:
    "Advertising, lead generation and anything else spent to bring work in.",
  overhead:
    "Rent, utilities, vehicles, insurance, office and general costs — everything else it takes to run the business.",
  [DO_NOT_MAP_CODE]:
    "Accounts that should reach no KPI at all. Excluded from every card, and from both sides of reconciliation.",
};

export const isLikelySubtypeMatch = (
  accountSubType: string | null,
  qboSubtypeMatches: string[],
): boolean =>
  accountSubType !== null &&
  qboSubtypeMatches.some(
    (subType) => subType.toLowerCase() === accountSubType.toLowerCase(),
  );
